import { useState } from "react";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface InlineEditDateProps {
  value: string;
  onSave: (value: string) => void;
  label: string;
  formatDate: (v: string) => string;
  emptyPlaceholder?: string;
  className?: string;
  clearable?: boolean;
}

function parseISODate(s: string | null | undefined): Date | undefined {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function formatISODate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function InlineEditDate({
  value,
  onSave,
  label,
  formatDate,
  emptyPlaceholder = "Set date",
  className,
  clearable = true,
}: InlineEditDateProps) {
  const [open, setOpen] = useState(false);

  const selected = parseISODate(value);

  const handleSelect = (d: Date | undefined) => {
    if (!d) return;
    const iso = formatISODate(d);
    setOpen(false);
    if (iso !== value) onSave(iso);
  };

  const handleClear = () => {
    setOpen(false);
    if (value) onSave("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "group inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors",
            className
          )}
        >
          <span className="text-xs text-muted-foreground">{label}:</span>
          <span className={cn("text-sm", !value && "italic text-muted-foreground/60")}>
            {value ? formatDate(value) : emptyPlaceholder}
          </span>
          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-3" align="start" collisionPadding={8} sideOffset={4}>
        <div className="flex flex-col gap-2 items-start">
          <span className="text-xs text-muted-foreground font-medium">{label}</span>
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            captionLayout="label"
            className="w-[calc(7*var(--cell-size)+1.5rem)]"
            onSelect={handleSelect}
          />
          <div className="flex justify-between gap-2 w-full">
            {clearable ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={!value}
                onClick={handleClear}
              >
                Clear
              </Button>
            ) : (
              <span />
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
